import { capitalize } from './format.utils';

/**
 * Creates a CSV blob from an array of objects
 *
 * `text/csv`: is the MIME type for CSV files
 * `application/vnd.ms-excel`: is the MIME type for CSV files that mascaraed as Excel files.
 *
 * Note that if PapaParse is able to parse the file then it can be supported, just not all excel files will be parsable.
 */
export const SupportedFileParsingTypes = [
	'text/csv',
	'application/vnd.ms-excel',
];

export function generateCsvAndBlob<T extends object>(
	data: T[],
	order: (keyof T)[] = [],
): Blob {
	if (!data.length) throw Error('No data found to create a CSV with');

	const headers = order ?? Object.keys(data[0]);
	const header = headers.sort(
		(a, b) => order.indexOf(a as keyof T) - order.indexOf(b as keyof T),
	);
	const rows = [header.map((h) => capitalize(h as string)).join(',')];

	const orderedKeys = (data: T) =>
		Object.entries(data)
			.sort(
				([aKey], [bKey]) =>
					order.indexOf(aKey as keyof T) -
					order.indexOf(bKey as keyof T),
			)
			.map(([, value]) => value ?? '-');

	data.forEach((d) =>
		rows.push(
			orderedKeys({
				...order.reduce((v, c) => ({ ...v, [c]: '-' }), {}),
				...d,
			}).join(','),
		),
	);

	return createCsvBlob(rows);
}

export const formatForCsv = (rows: string[]) => rows.join('\r\n');

export const createCsvBlob = (rows: string[] | string) => {
	if (typeof rows === 'string') {
		return new Blob([rows], { type: 'text/csv' });
	}
	return new Blob([formatForCsv(rows)], { type: 'text/csv' });
};

export const fileName = (prepend: string, ext: string) =>
	`${prepend}_${new Date().toISOString()}.${ext}`.replace(/ /g, '_');
